import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, ClipboardList, DollarSign, Trash2, Printer, Download, Pencil, AlertTriangle, Upload } from "lucide-react";
import { exportToCSV, handlePrint } from "../../utils/exportUtils";

const AGE_GROUPS = ["mini", "junior", "senior", "adult"];

const RegistrationsPage = () => {
  const [contestants, setContestants] = useState([]);
  const [playdays, setPlaydays] = useState([]);
  const [events, setEvents] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Edit state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    event_ids: [],
    team_roping_partner_id: "",
    team_roping_partner: "",
    ribbon_roping_partner_id: "",
    ribbon_roping_partner: "",
    roping_fee: false,
    arena_fee: true
  });
  
  // Confirm remove from draw dialog
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removedEvents, setRemovedEvents] = useState([]);
  const [pendingEditData, setPendingEditData] = useState(null);
  
  const [formData, setFormData] = useState({
    contestant_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age_group: "mini",
    event_ids: [],
    team_roping_partner_id: "",
    team_roping_partner: "",
    ribbon_roping_partner_id: "",
    ribbon_roping_partner: "",
    roping_fee: false,
    arena_fee: true
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchEntries();
    }
  }, [selectedPlayday]);

  const fetchInitialData = async () => {
    try {
      const [contestantsRes, playdaysRes, eventsRes] = await Promise.all([
        axios.get(`${API}/contestants`),
        axios.get(`${API}/playdays`),
        axios.get(`${API}/events`)
      ]);
      setContestants(contestantsRes.data);
      setPlaydays(playdaysRes.data);
      setEvents(eventsRes.data);
      if (playdaysRes.data.length > 0) {
        setSelectedPlayday(playdaysRes.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/entries?playday_id=${selectedPlayday}`);
      setEntries(response.data);
    } catch (error) {
      toast.error("Failed to fetch entries");
    }
  };

  // Sort contestants alphabetically by name
  const sortedContestants = [...contestants].sort((a, b) => {
    const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
    const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const handleContestantSelect = (contestantId) => {
    if (!contestantId || contestantId === "new") {
      setFormData(prev => ({
        ...prev,
        contestant_id: "",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        age_group: "mini"
      }));
      return;
    }
    const contestant = contestants.find(c => c.id === contestantId);
    if (contestant) {
      setFormData(prev => ({
        ...prev,
        contestant_id: contestantId,
        first_name: contestant.first_name,
        last_name: contestant.last_name,
        email: contestant.email || "",
        phone: contestant.phone || "",
        age_group: contestant.age_group
      }));
    }
  };

  const toggleEvent = (eventId) => {
    const newEventIds = formData.event_ids.includes(eventId)
      ? formData.event_ids.filter(id => id !== eventId)
      : [...formData.event_ids, eventId];
    
    // Auto-enable roping fee if any roping event is selected
    const hasRopingEvent = newEventIds.some(eid => {
      const event = events.find(e => e.id === eid);
      return event?.name?.toLowerCase().includes('roping');
    });
    
    setFormData(prev => ({
      ...prev,
      event_ids: newEventIds,
      roping_fee: hasRopingEvent
    }));
  };

  const calculateTotal = () => {
    const eventFees = formData.event_ids.reduce((sum, eid) => {
      const event = events.find(e => e.id === eid);
      return sum + (event?.fee || 3);
    }, 0);
    const ropingFee = formData.roping_fee ? 3 : 0;
    const arenaFee = formData.arena_fee ? 3 : 0;
    return eventFees + ropingFee + arenaFee;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.event_ids.length === 0) {
      toast.error("Please select at least one event");
      return;
    }

    setSubmitting(true);
    try {
      let contestantId = formData.contestant_id;
      
      // Create contestant if new
      if (!contestantId) {
        const contestantRes = await axios.post(`${API}/contestants`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email || null,
          phone: formData.phone || null,
          age_group: formData.age_group,
          is_member: false
        });
        contestantId = contestantRes.data.id;
        // Refresh contestants list
        const updatedContestants = await axios.get(`${API}/contestants`);
        setContestants(updatedContestants.data);
      }

      // Create entry
      await axios.post(`${API}/entries`, {
        contestant_id: contestantId,
        playday_id: selectedPlayday,
        event_ids: formData.event_ids,
        team_roping_partner_id: formData.team_roping_partner_id || null,
        team_roping_partner: formData.team_roping_partner || null,
        ribbon_roping_partner_id: formData.ribbon_roping_partner_id || null,
        ribbon_roping_partner: formData.ribbon_roping_partner || null,
        roping_fee: formData.roping_fee,
        arena_fee: formData.arena_fee
      });

      toast.success("Registration added successfully!");
      setIsDialogOpen(false);
      resetForm();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Delete this registration?")) return;
    try {
      await axios.delete(`${API}/entries/${entryId}`);
      toast.success("Registration deleted");
      fetchEntries();
    } catch (error) {
      toast.error("Failed to delete registration");
    }
  };

  // Edit handlers
  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditFormData({
      event_ids: [...entry.event_ids],
      team_roping_partner_id: entry.team_roping_partner_id || "",
      team_roping_partner: entry.team_roping_partner || "",
      ribbon_roping_partner_id: entry.ribbon_roping_partner_id || "",
      ribbon_roping_partner: entry.ribbon_roping_partner || "",
      roping_fee: entry.roping_fee,
      arena_fee: entry.arena_fee
    });
    setIsEditDialogOpen(true);
  };

  const toggleEditEvent = (eventId) => {
    const newEventIds = editFormData.event_ids.includes(eventId)
      ? editFormData.event_ids.filter(id => id !== eventId)
      : [...editFormData.event_ids, eventId];
    
    // Auto-enable roping fee if any roping event is selected
    const hasRopingEvent = newEventIds.some(eid => {
      const event = events.find(e => e.id === eid);
      return event?.name?.toLowerCase().includes('roping');
    });
    
    setEditFormData(prev => ({
      ...prev,
      event_ids: newEventIds,
      roping_fee: hasRopingEvent
    }));
  };

  const calculateEditTotal = () => {
    const eventFees = editFormData.event_ids.reduce((sum, eid) => {
      const event = events.find(e => e.id === eid);
      return sum + (event?.fee || 3);
    }, 0);
    const ropingFee = editFormData.roping_fee ? 3 : 0;
    const arenaFee = editFormData.arena_fee ? 3 : 0;
    return eventFees + ropingFee + arenaFee;
  };

  const handleEditPartnerSelect = (field, contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    const partnerName = contestant ? `${contestant.first_name} ${contestant.last_name}` : "";
    
    if (field === "team") {
      setEditFormData(prev => ({
        ...prev,
        team_roping_partner_id: contestantId === "none" ? "" : contestantId,
        team_roping_partner: contestantId === "none" ? "" : partnerName
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        ribbon_roping_partner_id: contestantId === "none" ? "" : contestantId,
        ribbon_roping_partner: contestantId === "none" ? "" : partnerName
      }));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (editFormData.event_ids.length === 0) {
      toast.error("Please select at least one event");
      return;
    }

    // Check if events were removed
    const oldEventIds = new Set(editingEntry.event_ids);
    const newEventIds = new Set(editFormData.event_ids);
    const removedEventIds = [...oldEventIds].filter(id => !newEventIds.has(id));
    
    if (removedEventIds.length > 0) {
      // Show confirmation dialog for removing from draws
      setRemovedEvents(removedEventIds);
      setPendingEditData({...editFormData});
      setShowRemoveConfirm(true);
      return;
    }

    // No events removed, proceed with update
    await submitEdit(editFormData, false);
  };

  const submitEdit = async (data, removeFromDraws) => {
    setSubmitting(true);
    try {
      const response = await axios.put(`${API}/entries/${editingEntry.id}`, {
        ...data,
        remove_from_draws: removeFromDraws
      });
      
      const result = response.data;
      
      if (result.draws_affected && result.draws_affected.length > 0) {
        const eventNames = result.draws_affected.map(d => d.event_name).join(", ");
        toast.success(`Registration updated. Removed from draws: ${eventNames}`);
      } else if (result.added_events && result.added_events.length > 0) {
        toast.success("Registration updated. Use 'Add to Draw' in Draws page to include new events.");
      } else {
        toast.success("Registration updated successfully!");
      }
      
      setIsEditDialogOpen(false);
      setShowRemoveConfirm(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmRemoveFromDraws = () => {
    submitEdit(pendingEditData, true);
  };

  const handleKeepInDraws = () => {
    submitEdit(pendingEditData, false);
  };

  const closeEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingEntry(null);
    setShowRemoveConfirm(false);
    setRemovedEvents([]);
    setPendingEditData(null);
  };

  const resetForm = () => {
    setFormData({
      contestant_id: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      age_group: "mini",
      event_ids: [],
      team_roping_partner_id: "",
      team_roping_partner: "",
      ribbon_roping_partner_id: "",
      ribbon_roping_partner: "",
      roping_fee: false,
      arena_fee: true
    });
  };

  const getContestantName = (contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    return contestant ? `${contestant.first_name} ${contestant.last_name}` : "";
  };

  const handlePartnerSelect = (field, contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    const partnerName = contestant ? `${contestant.first_name} ${contestant.last_name}` : "";
    
    if (field === "team") {
      setFormData(prev => ({
        ...prev,
        team_roping_partner_id: contestantId === "none" ? "" : contestantId,
        team_roping_partner: contestantId === "none" ? "" : partnerName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        ribbon_roping_partner_id: contestantId === "none" ? "" : contestantId,
        ribbon_roping_partner: contestantId === "none" ? "" : partnerName
      }));
    }
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.name : eventId;
  };

  const getSelectedPlaydayName = () => {
    const playday = playdays.find(p => p.id === selectedPlayday);
    return playday ? `${playday.name} (${playday.date})` : "";
  };

  const selectedPlaydayObj = playdays.find(p => p.id === selectedPlayday);
  const availableEvents = selectedPlaydayObj 
    ? events.filter(e => selectedPlaydayObj.event_ids.includes(e.id))
    : events;

  const totalFees = entries.reduce((sum, e) => sum + e.total_fees, 0);

  const handleExportRegistrations = () => {
    const exportData = entries.map(entry => ({
      contestant: entry.contestant_name,
      events: entry.event_ids.map(eid => getEventName(eid)).join("; "),
      team_partner: entry.team_roping_partner || "",
      ribbon_partner: entry.ribbon_roping_partner || "",
      total_fees: entry.total_fees.toFixed(2),
      status: entry.is_paid ? "PAID" : "UNPAID"
    }));
    
    exportToCSV(
      exportData, 
      `registrations_${getSelectedPlaydayName().replace(/[^a-z0-9]/gi, '_')}.csv`,
      ["contestant", "events", "team_partner", "ribbon_partner", "total_fees", "status"]
    );
    toast.success("Registrations exported to CSV");
  };

  const handlePrintRegistrations = () => {
    handlePrint("Registrations", getSelectedPlaydayName());
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedPlayday) {
      toast.error("Select a play day first");
      return;
    }
    
    setCsvUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("playday_id", selectedPlayday);
      
      const response = await axios.post(`${API}/entries/upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const r = response.data;
      let msg = `Created ${r.entries_created} entries`;
      if (r.created_contestants > 0) msg += `, ${r.created_contestants} new contestants`;
      if (r.skipped > 0) msg += `, ${r.skipped} skipped (duplicates)`;
      toast.success(msg);
      
      if (r.errors?.length > 0) {
        toast.warning(`${r.errors.length} warnings: ${r.errors.slice(0, 3).join("; ")}`);
      }
      
      fetchEntries();
      fetchContestants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "CSV upload failed");
    } finally {
      setCsvUploading(false);
      e.target.value = "";
    }
  };

  const downloadCsvTemplate = () => {
    const template = "contestant,events,partner\nJohn Smith,Mini Barrels;Mini Poles,\nJane Doe,Team Roping;JR Barrels,John Smith\n";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "registration_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 print-container" data-testid="registrations-page">
      <div className="flex items-center justify-between no-print">
        <div>
          <h1 className="font-heading font-bold text-3xl text-text-primary">Registrations</h1>
          <p className="text-text-secondary mt-1">Add and manage contestant registrations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrintRegistrations} disabled={entries.length === 0} data-testid="print-registrations-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={handleExportRegistrations} disabled={entries.length === 0} data-testid="export-registrations-btn">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={downloadCsvTemplate} data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-2" />
            Template
          </Button>
          <Button
            variant="outline"
            disabled={csvUploading || !selectedPlayday}
            onClick={() => document.getElementById("csv-upload-input").click()}
            data-testid="upload-csv-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {csvUploading ? "Uploading..." : "Upload CSV"}
          </Button>
          <input
            id="csv-upload-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleCsvUpload}
          />
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-accent text-white hover:bg-accent/90" data-testid="add-registration-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Registration
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add Registration</DialogTitle>
                <p className="text-sm text-text-secondary">
                  Adding registration for: <span className="font-medium text-primary">{getSelectedPlaydayName()}</span>
                </p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="registration-form">
                {/* Contestant Selection */}
                <div className="space-y-2">
                  <Label>Select Contestant</Label>
                  <Select
                    value={formData.contestant_id || "new"}
                    onValueChange={handleContestantSelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select or add new" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">+ New Contestant</SelectItem>
                      {sortedContestants.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name} ({c.age_group})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* New Contestant Fields */}
                {!formData.contestant_id && (
                  <Card className="border-primary/20">
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm font-medium">New Contestant Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">First Name *</Label>
                          <Input
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Last Name *</Label>
                          <Input
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Email</Label>
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Phone</Label>
                          <Input
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Age Group *</Label>
                          <Select
                            value={formData.age_group}
                            onValueChange={(value) => setFormData({ ...formData, age_group: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {AGE_GROUPS.map((ag) => (
                                <SelectItem key={ag} value={ag} className="capitalize">
                                  {ag.charAt(0).toUpperCase() + ag.slice(1)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Events Selection */}
                <div className="space-y-2">
                  <Label>Select Events ($3 each)</Label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                    {availableEvents.map((event) => (
                      <div key={event.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`reg-event-${event.id}`}
                          checked={formData.event_ids.includes(event.id)}
                          onCheckedChange={() => toggleEvent(event.id)}
                        />
                        <label htmlFor={`reg-event-${event.id}`} className="text-sm cursor-pointer">
                          {event.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-text-muted">{formData.event_ids.length} events selected</p>
                </div>

                {/* Partners */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Team Roping Partner (Contestant 2)</Label>
                    <Select
                      value={formData.team_roping_partner_id || "none"}
                      onValueChange={(value) => handlePartnerSelect("team", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select partner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Partner</SelectItem>
                        {sortedContestants
                          .filter(c => c.id !== formData.contestant_id)
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.first_name} {c.last_name} ({c.age_group})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Ribbon Roping Partner (Contestant 2)</Label>
                    <Select
                      value={formData.ribbon_roping_partner_id || "none"}
                      onValueChange={(value) => handlePartnerSelect("ribbon", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select partner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Partner</SelectItem>
                        {sortedContestants
                          .filter(c => c.id !== formData.contestant_id)
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.first_name} {c.last_name} ({c.age_group})
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Fees */}
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="admin-roping-fee"
                      checked={formData.roping_fee}
                      onCheckedChange={(checked) => setFormData({ ...formData, roping_fee: checked })}
                    />
                    <label htmlFor="admin-roping-fee" className="text-sm">Roping Fee $3</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="admin-arena-fee"
                      checked={formData.arena_fee}
                      disabled
                    />
                    <label htmlFor="admin-arena-fee" className="text-sm">Arena Fee $3 (Required)</label>
                  </div>
                </div>

                {/* Total & Submit */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-accent" />
                    <span className="font-heading font-bold text-lg">Total: ${calculateTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting || formData.event_ids.length === 0}
                      className="bg-accent text-white hover:bg-accent/90"
                      data-testid="submit-registration"
                    >
                      {submitting ? "Adding..." : "Add Registration"}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block print-header">
        <h1 className="font-heading font-bold text-2xl">TTP Play Day - Registrations</h1>
        <p>{getSelectedPlaydayName()}</p>
        <p className="mt-2">Total Entries: {entries.length} | Total Fees: ${totalFees.toFixed(2)}</p>
      </div>

      {/* Playday Selector */}
      <div className="flex gap-4 items-end no-print">
        <div className="w-64">
          <label className="text-sm font-medium text-text-primary mb-2 block">Select Play Day</label>
          <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
            <SelectTrigger data-testid="playday-select">
              <SelectValue placeholder="Select a play day" />
            </SelectTrigger>
            <SelectContent>
              {playdays.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name} ({p.date})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Card className="border-primary/20 flex-1 max-w-xs">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-text-secondary text-sm">Total Fees</span>
              <span className="font-heading font-bold text-lg text-accent">${totalFees.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-xl border border-primary/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50">
              <TableHead className="font-heading font-bold text-text-primary">Contestant</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Events</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Partners</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Fees</TableHead>
              <TableHead className="font-heading font-bold text-text-primary">Status</TableHead>
              <TableHead className="font-heading font-bold text-text-primary text-right no-print">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-text-secondary">
                  <ClipboardList className="h-8 w-8 mx-auto mb-2 text-text-muted" />
                  No registrations for this play day
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow key={entry.id} className="hover:bg-surface-muted" data-testid={`entry-row-${entry.id}`}>
                  <TableCell className="font-medium text-text-primary">
                    {entry.contestant_name}
                  </TableCell>
                  <TableCell className="text-text-secondary text-sm max-w-xs">
                    <div className="space-y-0.5">
                      {entry.event_ids.map((eid) => (
                        <div key={eid}>{getEventName(eid)}</div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-text-secondary text-sm">
                    {entry.team_roping_partner && <div>Team: {entry.team_roping_partner}</div>}
                    {entry.ribbon_roping_partner && <div>Ribbon: {entry.ribbon_roping_partner}</div>}
                    {!entry.team_roping_partner && !entry.ribbon_roping_partner && "-"}
                  </TableCell>
                  <TableCell className="font-mono font-bold text-text-primary">
                    ${entry.total_fees.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    {entry.is_paid ? (
                      <span className="px-2 py-1 bg-status-success/20 text-status-success rounded text-xs font-medium">
                        Paid
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-status-warning/20 text-status-warning rounded text-xs font-medium">
                        Unpaid
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right no-print">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(entry)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-entry-${entry.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEntry(entry.id)}
                        className="h-8 w-8 p-0 text-status-error hover:text-status-error"
                        data-testid={`delete-entry-${entry.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit Registration Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { if (!open) closeEditDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Registration</DialogTitle>
            {editingEntry && (
              <p className="text-sm text-text-secondary">
                Editing: <span className="font-medium text-primary">{editingEntry.contestant_name}</span>
              </p>
            )}
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-registration-form">
            {/* Events Selection */}
            <div className="space-y-2">
              <Label>Select Events ($3 each)</Label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                {availableEvents.map((event) => (
                  <div key={event.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-event-${event.id}`}
                      checked={editFormData.event_ids.includes(event.id)}
                      onCheckedChange={() => toggleEditEvent(event.id)}
                    />
                    <label htmlFor={`edit-event-${event.id}`} className="text-sm cursor-pointer">
                      {event.name}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-text-muted">{editFormData.event_ids.length} events selected</p>
            </div>

            {/* Partners */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Team Roping Partner (Contestant 2)</Label>
                <Select
                  value={editFormData.team_roping_partner_id || "none"}
                  onValueChange={(value) => handleEditPartnerSelect("team", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Partner</SelectItem>
                    {sortedContestants
                      .filter(c => c.id !== editingEntry?.contestant_id)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name} ({c.age_group})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ribbon Roping Partner (Contestant 2)</Label>
                <Select
                  value={editFormData.ribbon_roping_partner_id || "none"}
                  onValueChange={(value) => handleEditPartnerSelect("ribbon", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Partner</SelectItem>
                    {sortedContestants
                      .filter(c => c.id !== editingEntry?.contestant_id)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.first_name} {c.last_name} ({c.age_group})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Fees */}
            <div className="flex gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-roping-fee"
                  checked={editFormData.roping_fee}
                  onCheckedChange={(checked) => setEditFormData({ ...editFormData, roping_fee: checked })}
                />
                <label htmlFor="edit-roping-fee" className="text-sm">Roping Fee $3</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="edit-arena-fee"
                  checked={editFormData.arena_fee}
                  disabled
                />
                <label htmlFor="edit-arena-fee" className="text-sm">Arena Fee $3 (Required)</label>
              </div>
            </div>

            {/* Total & Submit */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-accent" />
                <span className="font-heading font-bold text-lg">Total: ${calculateEditTotal().toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeEditDialog}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting || editFormData.event_ids.length === 0}
                  className="bg-primary text-primary-foreground hover:bg-primary-hover"
                  data-testid="submit-edit-registration"
                >
                  {submitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove from Draws Dialog */}
      <Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-status-warning" />
              Remove from Draws?
            </DialogTitle>
            <DialogDescription className="text-text-secondary">
              You removed the following events from this registration:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ul className="list-disc list-inside space-y-1">
              {removedEvents.map(eventId => (
                <li key={eventId} className="text-text-primary font-medium">
                  {getEventName(eventId)}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-text-secondary">
              Would you like to also remove this contestant from the draws for these events?
            </p>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleKeepInDraws} disabled={submitting}>
              Keep in Draws
            </Button>
            <Button 
              onClick={handleConfirmRemoveFromDraws} 
              disabled={submitting}
              className="bg-status-warning text-white hover:bg-status-warning/90"
              data-testid="confirm-remove-from-draws"
            >
              {submitting ? "Removing..." : "Remove from Draws"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationsPage;
